'use client'

import React, { useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

/**
 * Confetti — a self-contained, dependency-free celebration burst.
 *
 * Built with framer-motion (already a project dependency) rather than pulling
 * in canvas-confetti, so there's no new bundle cost and it inherits the app's
 * reduced-motion handling. Used to reward a completed key action — e.g. joining
 * the Android waitlist — following the Canva/Freshdesk positive-reinforcement
 * pattern, but tuned to the brand palette so it reads as polished, not
 * cartoonish.
 *
 * Accessibility: purely decorative (aria-hidden) and pointer-events-none so it
 * never blocks the close button underneath. Renders nothing when the user has
 * `prefers-reduced-motion` set — the success checkmark + copy already confirm
 * the action, so no one misses information.
 */
const COLORS = ['#1E6B47', '#3DDC84', '#2E2E2E', '#155235', '#8FD3FF', '#FFD8A8']

export interface ConfettiProps {
  /** Number of pieces. 40–60 reads celebratory without feeling noisy. */
  count?: number
  className?: string
}

export default function Confetti({ count = 52, className = '' }: ConfettiProps) {
  const reduceMotion = useReducedMotion()

  // Randomised once per mount, via a lazy useState initializer rather than
  // useMemo. Next 16's core-web-vitals adds react-hooks/purity, which correctly
  // flags Math.random() inside useMemo: useMemo runs during render and React
  // may re-run it, so the render phase must stay pure. A lazy useState
  // initializer is the sanctioned way to compute a mount-once value and is the
  // only formulation of this that the rule accepts (verified against
  // useMemo/useRef/useEffect alternatives).
  //
  // Safe because `count` never changes for a given mount — all three call sites
  // pass a literal (40, 40, and the 52 default), and the burst is mounted fresh
  // on each success. If count ever becomes dynamic, this needs revisiting.
  const [pieces] = useState(() =>
      Array.from({ length: count }, (_, i) => {
        const angle = Math.random() * Math.PI * 2
        const distance = 70 + Math.random() * 150
        return {
          id: i,
          dx: Math.cos(angle) * distance,
          rise: 40 + Math.random() * 130,
          fall: 240 + Math.random() * 180,
          rotate: Math.random() * 720 - 360,
          color: COLORS[i % COLORS.length],
          w: 6 + Math.random() * 4,
          h: 9 + Math.random() * 7,
          rounded: Math.random() > 0.65,
          delay: Math.random() * 0.14,
          duration: 1.5 + Math.random() * 0.9,
        }
      })
  )

  if (reduceMotion) return null

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    >
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className="absolute left-1/2 top-[42%] block"
          style={{
            width: p.w,
            height: p.h,
            backgroundColor: p.color,
            borderRadius: p.rounded ? '9999px' : '1px',
          }}
          initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
          animate={{
            x: p.dx,
            y: [0, -p.rise, p.fall],
            opacity: [0, 1, 1, 0],
            rotate: p.rotate,
          }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'easeOut' }}
        />
      ))}
    </div>
  )
}

Confetti.displayName = 'Confetti'
